import * as React from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "../../components/mui";
import { BarChartRoundedIcon, SearchRoundedIcon, RefreshRoundedIcon } from "../../components/icons";

import PageLayout from "../../components/shared/PageLayout";
import PageHeader from "../../components/shared/PageHeader";
import DataTableCard from "../../components/shared/DataTableCard";
import { useToast } from "../../components/shared/ToastProvider";

import "../../styles/pages/expenseSummary.css";

// Hardcoded filter options - Backend developer will replace with API calls
const CURRENCY_OPTIONS = [
  { value: "PHP", label: "PHP" },
  { value: "USD", label: "USD" },
  { value: "EUR", label: "EUR" }
];

const YEAR_OPTIONS = [
  { value: "2025", label: "2025" },
  { value: "2024", label: "2024" },
  { value: "2023", label: "2023" }
];

const CONDO_COMPANY_OPTIONS = [
  { value: "all", label: "All" },
  { value: "log", label: "LOG" },
  { value: "jk", label: "JK" },
  { value: "ousan", label: "Ousan Solution Inc." }
];

const CONDO_SHARING_OPTIONS = [
  { value: "all", label: "All" },
  { value: "default", label: "Default" },
  { value: "shared", label: "Shared" }
];

const STAT_MODE_OPTIONS = [
  { value: "default", label: "Default" },
  { value: "custom", label: "Custom" }
];

// Hardcoded expense data - Backend developer will replace with API calls
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const OVERALL_EXPENSES_DATA = [
  { month: "January", currency: "PHP", amount: "380,706.91" },
  { month: "February", currency: "PHP", amount: "470,967.74" },
  { month: "March", currency: "PHP", amount: "330,796.72" },
  { month: "April", currency: "PHP", amount: "423,684.15" },
  { month: "May", currency: "PHP", amount: "10,285,706.56" },
  { month: "June", currency: "PHP", amount: "176,594.30" },
  { month: "July", currency: "PHP", amount: "73,140.47" },
  { month: "August", currency: "PHP", amount: "76,190.89" },
  { month: "September", currency: "PHP", amount: "43,362.99" },
  { month: "October", currency: "PHP", amount: "33,664.56" },
  { month: "November", currency: "PHP", amount: "31,841.60" },
  { month: "December", currency: "PHP", amount: "40,871.81" }
];

const RENTAL_EXPENSES_DATA = [
  { month: "January", currency: "PHP", amount: "0.00" },
  { month: "February", currency: "PHP", amount: "0.00" },
  { month: "March", currency: "PHP", amount: "0.00" },
  { month: "April", currency: "PHP", amount: "0.00" },
  { month: "May", currency: "PHP", amount: "0.00" },
  { month: "June", currency: "PHP", amount: "0.00" },
  { month: "July", currency: "PHP", amount: "0.00" },
  { month: "August", currency: "PHP", amount: "0.00" },
  { month: "September", currency: "PHP", amount: "0.00" },
  { month: "October", currency: "PHP", amount: "0.00" },
  { month: "November", currency: "PHP", amount: "0.00" },
  { month: "December", currency: "PHP", amount: "0.00" }
];

const MISC_EXPENSES_DATA = [
  { month: "January", currency: "PHP", amount: "152,672.86" },
  { month: "February", currency: "PHP", amount: "62,772.91" },
  { month: "March", currency: "PHP", amount: "126,159.16" },
  { month: "April", currency: "PHP", amount: "184,005.02" },
  { month: "May", currency: "PHP", amount: "49,651.57" },
  { month: "June", currency: "PHP", amount: "116,615.08" },
  { month: "July", currency: "PHP", amount: "28,952.94" },
  { month: "August", currency: "PHP", amount: "25,198.00" },
  { month: "September", currency: "PHP", amount: "3,699.00" },
  { month: "October", currency: "PHP", amount: "3,699.00" },
  { month: "November", currency: "PHP", amount: "3,699.00" },
  { month: "December", currency: "PHP", amount: "2,699.00" }
];

const UTILITY_EXPENSES_DATA = [
  { month: "January", currency: "PHP", amount: "228,034.05" },
  { month: "February", currency: "PHP", amount: "221,390.75" },
  { month: "March", currency: "PHP", amount: "204,637.56" },
  { month: "April", currency: "PHP", amount: "239,679.13" },
  { month: "May", currency: "PHP", amount: "156,056.99" },
  { month: "June", currency: "PHP", amount: "59,979.22" },
  { month: "July", currency: "PHP", amount: "44,188.43" },
  { month: "August", currency: "PHP", amount: "50,992.89" },
  { month: "September", currency: "PHP", amount: "39,663.99" },
  { month: "October", currency: "PHP", amount: "29,965.56" },
  { month: "November", currency: "PHP", amount: "28,142.60" },
  { month: "December", currency: "PHP", amount: "38,172.81" }
];

const ALLOWANCE_EXPENSES_DATA = [
  { month: "January", currency: "PHP", amount: "0.00" },
  { month: "February", currency: "PHP", amount: "0.00" },
  { month: "March", currency: "PHP", amount: "0.00" },
  { month: "April", currency: "PHP", amount: "0.00" },
  { month: "May", currency: "PHP", amount: "0.00" },
  { month: "June", currency: "PHP", amount: "0.00" },
  { month: "July", currency: "PHP", amount: "0.00" },
  { month: "August", currency: "PHP", amount: "0.00" },
  { month: "September", currency: "PHP", amount: "0.00" },
  { month: "October", currency: "PHP", amount: "0.00" },
  { month: "November", currency: "PHP", amount: "0.00" },
  { month: "December", currency: "PHP", amount: "0.00" }
];

// Calculate totals
const calculateTotal = (data) => {
  return data.reduce((sum, item) => {
    const amount = parseFloat(item.amount.replace(/,/g, ""));
    return sum + amount;
  }, 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * ExpenseTable Component
 * Reusable table for displaying monthly expense data
 */
function ExpenseTable({ title, data, currency }) {
  const total = calculateTotal(data);

  return (
    <DataTableCard
      title={title}
      showExport={false}
      showEntriesControl={false}
      showFooter={false}
      className="expense-summary-card"
    >
      <TableContainer className="data-table-wrapper">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Month</TableCell>
              <TableCell>Currency</TableCell>
              <TableCell align="right">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <TableRow hover key={index}>
                <TableCell>{row.month}</TableCell>
                <TableCell>{row.currency}</TableCell>
                <TableCell align="right">{row.amount}</TableCell>
              </TableRow>
            ))}
            <TableRow className="expense-summary-total-row">
              <TableCell colSpan={2} className="expense-summary-total-label">
                Total
              </TableCell>
              <TableCell align="right" className="expense-summary-total-amount">
                {total}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </DataTableCard>
  );
}

/**
 * Overall Condo Unit Expense Summary Page Component
 * Displays expense breakdown across multiple categories
 * UI/Layout only - Backend developer will add:
 * - API calls for fetching expense data based on filters
 * - Dynamic data loading and filtering
 * - Export functionality
 */
export default function ExpenseSummary({ onLogout, onNavigate }) {
  const { showToast } = useToast();

  const [filters, setFilters] = React.useState({
    currency: "PHP",
    year: "2025",
    condoCompany: "all",
    condoSharing: "all",
    statMode: "default"
  });

  const [errors, setErrors] = React.useState({
    currency: false,
    year: false,
    condoCompany: false,
    condoSharing: false,
    statMode: false
  });

  const handleFilterChange = (field) => (event) => {
    const value = event.target.value;
    setFilters((prev) => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handleSearch = () => {
    const newErrors = {
      currency: filters.currency === "",
      year: filters.year === "",
      condoCompany: filters.condoCompany === "",
      condoSharing: filters.condoSharing === "",
      statMode: filters.statMode === ""
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error)) {
      showToast({
        message: "Please fill in all required fields",
        severity: "error"
      });
      return;
    }

    // Placeholder for future integration
    showToast({
      message: "Expense summary loaded successfully",
      severity: "success"
    });
  };

  const handleReset = () => {
    setFilters({
      currency: "PHP",
      year: "2025",
      condoCompany: "all",
      condoSharing: "all",
      statMode: "default"
    });
    setErrors({
      currency: false,
      year: false,
      condoCompany: false,
      condoSharing: false,
      statMode: false
    });
  };

  return (
    <PageLayout activeKey="expense-summary" onLogout={onLogout} onNavigate={onNavigate} className="page-content">
      <PageHeader
        icon={BarChartRoundedIcon}
        title="Overall Condo Unit Expense Summary"
        summary="View comprehensive expense breakdown by category and month."
      />

      {/* Filters */}
      <Box className="content-box expense-summary-filters">
        <Box className="expense-summary-filters-form">
          <FormControl size="small" required error={errors.currency} className="expense-summary-filter">
            <InputLabel>Currency</InputLabel>
            <Select value={filters.currency} label="Currency" onChange={handleFilterChange("currency")}>
              {CURRENCY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" required error={errors.year} className="expense-summary-filter">
            <InputLabel>Year</InputLabel>
            <Select value={filters.year} label="Year" onChange={handleFilterChange("year")}>
              {YEAR_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" required error={errors.condoCompany} className="expense-summary-filter">
            <InputLabel>Condo Company</InputLabel>
            <Select value={filters.condoCompany} label="Condo Company" onChange={handleFilterChange("condoCompany")}>
              {CONDO_COMPANY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" required error={errors.condoSharing} className="expense-summary-filter">
            <InputLabel>Condo Sharing</InputLabel>
            <Select value={filters.condoSharing} label="Condo Sharing" onChange={handleFilterChange("condoSharing")}>
              {CONDO_SHARING_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" required error={errors.statMode} className="expense-summary-filter">
            <InputLabel>Stat Mode</InputLabel>
            <Select value={filters.statMode} label="Stat Mode" onChange={handleFilterChange("statMode")}>
              {STAT_MODE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box className="expense-summary-actions">
            <Button variant="contained" startIcon={<SearchRoundedIcon />} onClick={handleSearch}>
              Search
            </Button>
            <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={handleReset}>
              Reset
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Expense Tables Grid */}
      <Box className="expense-summary-grid">
        <ExpenseTable
          title="Overall Expenses By Month (Account Entry + Payment)"
          data={OVERALL_EXPENSES_DATA}
          currency={filters.currency}
        />
        <ExpenseTable
          title="Rental Expenses By Month (Account Entry)"
          data={RENTAL_EXPENSES_DATA}
          currency={filters.currency}
        />
        <ExpenseTable
          title="Misc Expenses By Month (Account Entry)"
          data={MISC_EXPENSES_DATA}
          currency={filters.currency}
        />
        <ExpenseTable
          title="Utility Expenses By Month (Account Entry)"
          data={UTILITY_EXPENSES_DATA}
          currency={filters.currency}
        />
        <ExpenseTable
          title="Allowance By Month (Payment Data)"
          data={ALLOWANCE_EXPENSES_DATA}
          currency={filters.currency}
        />
      </Box>
    </PageLayout>
  );
}
